import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

/**
 * SectionErrorBoundary — compact inline error boundary for widgets/sections.
 *
 * Unlike the full-page ErrorBoundary, this renders a small fallback in place
 * of just the broken widget so the rest of the dashboard keeps working.
 *
 * Usage:
 *   <SectionErrorBoundary label="Notice Board">
 *     <NoticeBoard role="admin" />
 *   </SectionErrorBoundary>
 */
class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[SectionErrorBoundary:${this.props.label || 'unknown'}]`, error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="rounded-2xl bg-amber-50/70 border border-amber-200 px-4 py-5 text-center"
          data-testid={`section-error-${(this.props.label || 'section').toLowerCase().replace(/\s+/g, '-')}`}
        >
          <AlertCircle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
          <p className="text-sm font-medium text-amber-800">
            {this.props.label ? `${this.props.label} couldn't load` : "This section couldn't load"}
          </p>
          <p className="text-xs text-amber-700/80 mt-1">The rest of your dashboard is still working.</p>
          <button
            onClick={this.handleRetry}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default SectionErrorBoundary;
